<template>
  <div id="reportWrapper">
    <loading-notification
      v-if="isLoading"
      message="Het rapport wordt geladen...">
    </loading-notification>

    <template v-else>
      <template v-if="report.public_mode">
        <h1 class="title is-5">{{ report.bundle.name }}</h1>
        <h2
          v-if="report.dossier && report.profile"
          class="subtitle is-7"
        >
          {{ dossier }}
        </h2>
        <hr>
      </template>

      <h2 class="title is-3">
        Rapport | {{ report.name }}

        <button
          class="button is-pulled-right"
          @click="toggle"
        >
        <span class="icon">
          <i class="fa" :class="(mode === 'tree') ? 'fa-table' : 'fa-stream'"></i>
        </span>
        </button>
      </h2>

      <template v-if="(isAdmin(account) || inScope(account, 9)) && !isPublic">
        <div class="field no-print">
          <button
            type="button"
            class="button is-info"
            @click="share">{{ accessCode.id ? 'Nieuwe link genereren' : 'Rapport publiekelijk delen' }}
          </button>

          <button
            v-if="accessCode.id"
            type="button"
            class="button is-danger ml-3"
            @click="revoke">Stop publiekelijk delen</button>

          <p v-show="accessCode.access_code" class="is-size-7">
            {{ sharingUrl }}

            <button
              id="copy"
              type="button"
              class="button"
              :class="{ 'is-success': copy }"
              v-clipboard:copy="sharingUrl"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError">
              <span class="icon">
                <i v-if="copy" class="fas fa-check"></i>
                <i v-if="!copy" class="fas fa-clipboard"></i>
              </span>
            </button>
          </p>
        </div>

        <hr>
      </template>

      <grid-report v-if="mode === 'grid'"></grid-report>

      <tree-report v-else></tree-report>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { authMixin } from '@/mixins'
import GridReport from '@/components/reports/GridReport'
import TreeReport from '@/components/reports/TreeReport'
import LoadingNotification from '@/components/modules/LoadingNotification'

export default {
  name: 'ShowReport',

  beforeRouteLeave (to, from, next) {
    this.$store.commit('reports/RESET')
    next()
  },

  mixins: [
    authMixin
  ],

  components: {
    GridReport,
    TreeReport,
    LoadingNotification
  },

  data () {
    return {
      copy: null,
      mode: 'grid',
      sortColumnId: 0,
      filterListHeight: 250,
      fixedHeaderThreshold: 370
    }
  },

  computed: {
    ...mapGetters({
      auth: 'account/auth',
      tree: 'datasets/tree',
      order: 'reports/order',
      report: 'reports/report',
      bundle: 'bundles/bundle',
      account: 'account/account',
      columns: 'columns/columns',
      filters: 'filters/filters',
      datasets: 'datasets/datasets',
      accessCode: 'accessCodes/accessCode'
    }),

    dossier () {
      if (!this.accessCode) return null

      let string = ''

      if (this.report.dossier) string += this.report.dossier + ' (' + this.report.dossier_crebo + ')'
      if (this.report.profile) string += ' / ' + this.report.profile + ' ( ' + this.report.profile_crebo + ')'
      if (this.report.valid_from) string += ' vanaf ' + this.report.valid_from

      return string
    },

    isLoading () {
      return !this.report ||
        this.columns.length === 0
    },

    sharingUrl () {
      return `${process.env.VUE_APP_API}/#/rapportage/${this.accessCode.access_code}`
    },

    filteredDatasets () {
      if (this.filters.length === 0) return this.datasets

      return this.datasets.filter((dataset) => {
        const result = this.filters.map((filter) => {
          const value = dataset.columns.find(x => {
            return (x) ? x.id === filter.columnId : filter.showEmpty
          })

          if (!value.value.value) return filter.showEmpty

          return filter.filter.includes(value.value.id)
        })

        return result.every(pass => pass)
      })
    },

    isPublic () {
      return typeof this.$route.params.accessCode !== 'undefined'
    }
  },

  created () {
    if (this.isPublic) {
      this.$store.dispatch('reports/loadWithAccessCode', {
        accessCode: this.$route.params.accessCode
      })
    } else {
      this.$store.dispatch('reports/load', {
        bundleId: this.$route.params.bundleId,
        reportId: this.$route.params.id
      })

      this.$store.dispatch('accessCodes/load', {
        bundleId: this.$route.params.bundleId,
        reportId: this.$route.params.id
      })
    }
  },

  beforeDestroy () {
    this.$store.commit('reports/RESET')
    this.$store.commit('columns/RESET')
    this.$store.commit('datasets/PURGE')
  },

  watch: {
    report () {
      if (this.report) {
        this.$store.dispatch('columns/load', {
          model: 'report',
          id: (this.isPublic) ? this.report.id : this.$route.params.id,
          bundleId: (this.isPublic) ? this.report.bundle.id : this.$route.params.bundleId
        })
      }
    },

    columns () {
      if (this.columns.length > 0) {
        const columnIds = this.columns.map(column => {
          return column.column.id
        })

        this.$store.dispatch('datasets/load', {
          tableId: this.report.table_id,
          bundleId: (this.isPublic) ? this.report.bundle.id : this.$route.params.bundleId,
          columnIds: columnIds
        })
      }
    }
  },

  methods: {
    toggle () {
      this.mode = (this.mode === 'grid') ? 'tree' : 'grid'
    },

    onCopy () {
      this.copy = true
    },

    onError () {
      this.copy = false
    },

    share () {
      if (this.accessCode.id) {
        this.$store.dispatch('accessCodes/regenerate')
      } else {
        this.$store.dispatch('accessCodes/save')
      }

      this.copy = null
    },

    revoke () {
      this.$store.dispatch('accessCodes/revoke')
      this.copy = null
    }
  }
}
</script>

<style scoped>
  .field button {
    margin-bottom: 15px;
  }

  #copy {
    top: -10px;
    margin: 0;
  }
</style>
